import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { catchError, map, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { Route } from '../enums/route.enum';
import { PartySearchContextService } from '@app/services/party-search/party-search-context.service';
import { UserRole } from '../enums/user-roles.enum';
import { UserService } from '../../services/user/user.service';

/**
 * Blocks CNH/Admin from customer routes until a successful party search has set context.
 * {@link UserRole.User} is always allowed for routes they may access via roleGuard.
 *
 * Waits for {@link UserService#getUserRoleGate$} so internal users are not treated as customers
 * while the user profile role is still loading after login.
 */
export const partySearchContextChildGuard: CanActivateChildFn = (childRoute) => {
  const userService = inject(UserService);
  const partyContext = inject(PartySearchContextService);
  const router = inject(Router);

  const requiresParty = childRoute.data?.['requiresPartySearch'] === true;
  if (!requiresParty) {
    return true;
  }

  const path = childRoute.routeConfig?.path;
  if (path === Route.FAQ || path === Route.CONTACT_US || path === Route.ADMIN_CONTACT_US) {
    return true;
  }

  return userService.getUserRoleGate$().pipe(
    take(1),
    map((role) => {
      if (role === UserRole.User) {
        return true;
      }

      if (partyContext.params() && partyContext.contractData()) {
        return true;
      }

      return router.parseUrl(`/${Route.SEARCH_USER}`);
    }),
    catchError(() => of(true)),
  );
};
